import { Component, OnInit, OnDestroy } from '@angular/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { FormBuilder, FormGroup, FormArray, AbstractControl } from '@angular/forms';
import { Job, Die } from 'app/models';
import { Subscription } from 'rxjs';
import { OrderService } from 'app/services/order.service';
import { AuthenticationService } from 'app/services/authentication.service';
import { BatchService } from 'app/services/batch.service';
import { ScannerService } from 'app/services/scanner.service';
import { HANDHELD } from 'ngrx/status-types';
import { TypeaheadMatch } from 'ngx-bootstrap/typeahead';

@Component({
  selector: 'app-change-machine-modal',
  templateUrl: './change-machine-modal.component.html',
  styleUrls: ['./change-machine-modal.component.scss'],
})
export class ChangeMachineModalComponent implements OnInit, OnDestroy {
  machineForm: FormGroup;
  jobs: any[] = [];
  selectAllChecked: boolean = false;
  selectedCount: number = 0;
  isAdmin: boolean = false;
  isSupervisor: boolean = false;
  selectedMachine: string = '';
  searchQuery: string = '';
  searchResults: any[] = [];
  selectedDie: string = '';
  dies: Die[] = [];
  machines = [
    { id: 'LASER', name: 'Laser' },
    { id: 'HORIZON', name: 'Horizon' },
  ];

  
  private formSubscription: Subscription;
  private initSubscriptions: Subscription[] = [];
  private dieListSubscription: Subscription;
  constructor(
    public bsModalRef: BsModalRef,
    private fb: FormBuilder,
    private orderService: OrderService,
    private batchService: BatchService,
    private scanner: ScannerService,
    private authService: AuthenticationService
  ) {
    this.createForm();
    this.isAdmin = this.authService.userIsAdmin();
    this.isSupervisor = this.authService.userIsSupervisor();
  }

  ngOnInit(): void {
    this.initializeForm();
    // this.scanner.setExclusiveMode(true);
    // console.log('Change Machine modal: Exclusive mode enabled');

    this.dieListSubscription = this.batchService.getDieList()
      .subscribe(dies => this.dies = dies || []);

    this.initSubscriptions = [
      this.formSubscription = this.getJobsFormArray().valueChanges
        .subscribe(() => {
          this.updateSelectionState();
        }),
      
      //TBD : Unsure what can be scanned since WO is not available at QUEUED status
      // this.scanner._changeCutMachineScanListener
      //   .subscribe(id => {
      //     const ID = id.replace(/[^A-Za-z0-9-]/g, '');
      //     const [l, t] = this.scanner._triggers;
      //     const location = this.scanner._locations[l];
      //     const type = this.scanner._types[t];
      //     console.log(`Change Machine ID ${ID} scanned at ${location}`);
      //     if(type === HANDHELD){
      //       // Split the scanned ID into orderId and jobId
      //       const [orderId, jobId] = ID.split('-');
      //       console.log(`Parsed OrderID: ${orderId}, JobID: ${jobId}`);
  
      //       // find/toggle the matching job
      //       const jobsFormArray = this.getJobsFormArray();
      //       const jobIndex = jobsFormArray.controls.findIndex(control => {
      //         const controlJobId = control.get('id').value;
      //         const controlOrderId = control.get('orderId').value;
      //         return (controlJobId === jobId && controlOrderId === orderId);
      //       });
  
      //       if (jobIndex !== -1) {
      //         const jobFormGroup = jobsFormArray.at(jobIndex);
      //         const currentValue = jobFormGroup.get('selected').value;
              
      //         jobFormGroup.patchValue({
      //           selected: !currentValue
      //         });
      //       } else {
      //         console.log(`No matching job found for OrderID: ${orderId}, JobID: ${jobId}`);
      //       }
      //     }
      //   })
    ];
  }

  ngOnDestroy(): void {
    this.scanner.setExclusiveMode(false);
    console.log('Change Machine modal: Exclusive mode disabled');
    
    if (this.formSubscription) {
      this.formSubscription.unsubscribe();
    }
    if (this.dieListSubscription) {
      this.dieListSubscription.unsubscribe();
    }

    this.initSubscriptions.forEach(subscription => {
      if (subscription) {
        subscription.unsubscribe();
      }
    });
  }

  initializeForm(): void {
    const formArray = this.jobs.map(job => 
      this.fb.group({
        selected: [false],
        id: [job.id],
        orderId: [job.orderId],
        horizonId: [job.horizonId],
        dueDate: [job.dueDate],
        finishType: [job.finishType],
        currentMachine: [job.machine || 'Unknown'],
        status: [job.status || 'Unknown'],
        laser_enabled: [job.oaDetails.material.laser_enabled && job.oaDetails.template.laser_enabled || false],
        die: [job.oaDetails.die && job.oaDetails.die.code || 'N/A'],
        is_custom_size: [job.oaDetails && job.oaDetails.template && job.oaDetails.template.is_custom_size == '1'],
      })
    );
    
    this.machineForm.setControl('jobs', this.fb.array(formArray));
  }

  private createForm(): void {
    this.machineForm = this.fb.group({
      jobs: this.fb.array([]),
      machine: ['']
    });
  }

  private getJobsFormArray(): FormArray {
    return this.machineForm.get('jobs') as FormArray;
  }

  private updateSelectionState(): void {
    const jobs = this.getJobsFormArray();
    const selectedJobs = jobs.controls.filter(control => control.get('selected').value);

    this.selectedCount = selectedJobs.length;
    const filtered = this.getFilteredJobs();
    this.selectAllChecked = filtered.length > 0 && filtered.every(control => control.get('selected').value);
  }

  toggleSelectAll(select: boolean): void {
    if (!this.isAdmin && !this.isSupervisor) return;

    const filtered = this.getFilteredJobs();
    filtered.forEach(control => {
      control.get('selected').setValue(select);
    });
  }

  clearAllSelections(): void {
    this.getJobsFormArray().controls.forEach(control => {
      control.get('selected').setValue(false);
    });
    this.updateSelectionState();
  }

  onDieFilterChange(): void {
    this.clearAllSelections();
    this.updateSearchResults();
  }

  changeMachine(): void {
    if (!this.selectedMachine) {
      console.error('No machine selected');
      return;
    }
    
    const selectedJobs = this.getSelectedJobs();
    
    // Disable exclusive mode before hiding the modal
    // this.scanner.setExclusiveMode(false);
    // console.log(`Change Machine modal: Moving jobs to ${this.selectedMachine}`);
    
    // Update jobs to selected machine status

    const updatedJobs = [];
    selectedJobs.forEach(job => {
      const user = JSON.parse(localStorage.getItem('currentUser'));
      const timestamp        = new Date().getTime().toString();

      let oaDetail = Object.assign({}, job.oaDetails, {
        material : Object.assign({}, job.oaDetails.material, {
          laser_enabled: this.selectedMachine === 'LASER' ? true : false
        }),
        template : Object.assign({}, job.oaDetails.template, {
          laser_enabled: this.selectedMachine === 'LASER' ? true : false
        })
      });

      const updatedJob = Object.assign({}, job, {
        activity_history: [{
          activity: 'CHANGE_CUT_MACHINE',
          note    : "Changed Cut Machine to " + (this.selectedMachine === null ? 'default product cutting' : this.selectedMachine),
          timestamp,
          user: user.email
        },...job.activity_history],

        oaDetails: oaDetail,
        batchId: null,
        cutting: this.selectedMachine === 'LASER' ? 'laser' : 'horizon',
        cutMachine: this.selectedMachine === 'LASER' ? 'laser' : 'horizon',
      });

      updatedJobs.push(updatedJob);
    });

    console.log("🚀 ~ updatedJobs:", updatedJobs)
    this.orderService.updateJobs(updatedJobs);

    this.bsModalRef.hide();
  }

  private getSelectedJobs(): Job[] {
    const selectedJobIds = this.getJobsFormArray().controls
    .filter(control => control.get('selected').value)
    .map(control => control.get('id').value);    

    return this.jobs.filter(job => selectedJobIds.indexOf(job.id) !== -1);
  }

  handleCheckboxClick(event: MouseEvent, control: AbstractControl): void {
    // user is admin, allow normal checkbox behavior
    if (this.isAdmin || this.isSupervisor) {
      return;
    }

    // non-admin users:
    // checkbox is currently checked, allow unchecking
    // checkbox is unchecked, prevent checking
    if (!control.get('selected').value) {
      event.preventDefault();
      return;
    }
  }
  
  setSelectedMachine(machineId: string): void {
    this.selectedMachine = machineId;
    // Reset selections when changing machines
    this.toggleSelectAll(false);
    // Reset die filter when changing machine
    this.selectedDie = '';
    // Refresh search suggestions to reflect new filters
    this.updateSearchResults();
  }
  
  getMachineName(machineId: string): string {
    const machine = this.machines.find(m => m.id === machineId);
    return machine ? machine.name : 'Selected Machine';
  }

  isReadyToSubmit(): boolean {
    return this.selectedCount > 0 && !!this.selectedMachine && !this.isHorizonBlockedByDie() && !this.isHorizonBlockedByCustomSize();
  }

  isHorizonBlockedByDie(): boolean {
    if (this.selectedMachine !== 'HORIZON' || this.selectedCount === 0) {
      return false;
    }
    const selectedControls = this.getJobsFormArray().controls.filter(c => c.get('selected').value);

    for (const control of selectedControls) {
      const dieCode = control.get('die').value;
    
      if (!dieCode || dieCode === 'N/A') continue;
    
      const die = this.dies.find(d => d.id === dieCode);
      if (die && die.isAvailable === false) {
        return true;
      }
    }
    return false;
  }

  isHorizonBlockedByCustomSize(): boolean {
    if (this.selectedMachine !== 'HORIZON' || this.selectedCount === 0) {
      return false;
    }
    const selectedControls = this.getJobsFormArray().controls.filter(c => c.get('selected').value);
    return selectedControls.some(c => c.get('is_custom_size').value === true || c.get('is_custom_size').value === '1');
  }

  getSelectedJobsWithDieOff(): { jobId: string; orderId: string; dieCode: string }[] {
    if (this.selectedMachine !== 'HORIZON') return [];

    const selectedControls = this.getJobsFormArray().controls.filter(c => c.get('selected').value);
    const result = [];

    for (const control of selectedControls) {
      const dieCode = control.get('die').value;
      
      if (!dieCode || dieCode === 'N/A') continue;

      const die = this.dies.find(d => d.id === dieCode);
      if (die && die.isAvailable === false) {
        result.push({
          jobId: control.get('id').value,
          orderId: control.get('orderId').value,
          dieCode: dieCode
        });
      }
    }
    return result;
  }

  getSelectedCustomSizeJobs(): { jobId: string; orderId: string }[] {
    if (this.selectedMachine !== 'HORIZON') return [];
    const selectedControls = this.getJobsFormArray().controls.filter(c => c.get('selected').value);
    return selectedControls
      .filter(c => c.get('is_custom_size').value === true || c.get('is_custom_size').value === '1')
      .map(c => ({ jobId: c.get('id').value, orderId: c.get('orderId').value }));
  }

  getFilteredJobs(): AbstractControl[] {
    const controls = this.getJobsFormArray().controls;

    if (!this.selectedMachine && !this.selectedDie) {
      return controls;
    }
    
    return controls.filter(control => {
      const isLaserEnabled = control.get('laser_enabled').value;
      const isCustomSize = control.get('is_custom_size').value === true || control.get('is_custom_size').value === '1';

      let matchesMachine = true;
      if (this.selectedMachine === 'LASER') {
        // For Laser machine change, only show jobs that are horizon
        matchesMachine = !isLaserEnabled;
      } else if (this.selectedMachine === 'HORIZON') {
        // For Horizon machine change, only show laser jobs; exclude custom size (no die)
        matchesMachine = isLaserEnabled && !isCustomSize;
      }

      const dieValue = control.get('die').value;
      const matchesDie = !this.selectedDie || dieValue === this.selectedDie;

      return matchesMachine && matchesDie;
    });
  }
  
  getOriginalIndex(control: AbstractControl): number {
    return this.getJobsFormArray().controls.indexOf(control);
  }

  getDieStatus(control: AbstractControl): string {
    const dieCode = control.get('die').value;
    if (!dieCode || dieCode === 'N/A') return 'N/A';
    const die = this.dies.find(d => d.id === dieCode);
    if (!die) return 'N/A';
    return die.isAvailable ? 'ON' : 'OFF';
  }

  isDieOff(control: AbstractControl): boolean {
    const dieCode = control.get('die').value;
    if (!dieCode || dieCode === 'N/A') return false;
    const die = this.dies.find(d => d.id === dieCode);
    return die ? die.isAvailable === false : false;
  }
  
  getNoJobsMessage(): string {
    if (this.selectedMachine === 'LASER') {
      return 'No non-laser jobs available to move to Laser.';
    } else if (this.selectedMachine === 'HORIZON') {
      return 'No laser-enabled jobs available to move to Horizon.';
    }
    return 'No jobs available.';
  }

  // Unique list of dies for dropdown
  getAvailableDies(): string[] {
    const controls = this.getJobsFormArray().controls;
    const filteredByMachine = controls.filter(control => {
      const isLaserEnabled = control.get('laser_enabled').value;
      if (this.selectedMachine === 'LASER') {
        return !isLaserEnabled;
      } else if (this.selectedMachine === 'HORIZON') {
        return isLaserEnabled;
      }
      return true;
    });
    const dies = filteredByMachine
      .map(ctrl => ctrl.get('die').value)
      .filter(v => !!v && v !== 'N/A');
    return Array.from(new Set(dies)).sort();
  }

  updateSearchResults(): void {
    if (!this.searchQuery || this.searchQuery.length < 1) {
      this.searchResults = [];
      return;
    }
    
    // Get jobs filtered by the selected machine
    const filteredJobs = this.getFilteredJobs();
    const query = this.searchQuery.toLowerCase();
    
    this.searchResults = filteredJobs
      .filter(jobControl => {
        const jobId = jobControl.get('id').value.toString().toLowerCase();
        const orderId = jobControl.get('orderId').value.toString().toLowerCase();
        
        return jobId.includes(query) || orderId.includes(query);
      })
      .map(jobControl => ({
        id: jobControl.get('id').value,
        orderId: jobControl.get('orderId').value,
        control: jobControl,
        displayText: `${jobControl.get('id').value} | ${jobControl.get('orderId').value}`
      }));
    console.log("🚀 ~ this.searchResults:", this.searchResults);
  }
  
  onSearchResultSelected(event: TypeaheadMatch): void {
    console.log("🚀 ~ onSearchResultSelected ~ event:", event)
    const selectedJob = event.item;
    const jobControl = selectedJob.control;
    
    jobControl.get('selected').setValue(true);
    
    // Reset search
    this.searchQuery = '';
    this.searchResults = [];
  }
} 


// WEBPACK FOOTER //
// /Users/itu/Desktop/impreso_updated_sheets/impresocloud/www/client/src/app/shared/modals/change-machine-modal/change-machine-modal.component.ts